import React from "react";
import { Box, Typography } from "@mui/material";
import { TargetData, ImageMetadata, NightMetadata } from "../types";
import { getFilterDisplay } from "../utils/getFilterDisplay";
import { useUI } from "../contexts/UIContext";

interface AcquisitionSummaryCardProps {
  imagesForView: ImageMetadata[];
  selectedTarget: TargetData;
  selectedNight: string | "total";
  getNightMeta: (
    target: TargetData,
    field:
      | keyof NightMetadata["acquisitionDetails"]
      | keyof NightMetadata["location"]
      | "cameraType",
    selectedNight?: string | "total"
  ) => (string | number)[] | "—";
  formatMultipleValues: (
    values: (string | number)[] | "—",
    unit?: string
  ) => React.ReactNode;
}

// Improved contrast function for text color on colored backgrounds
function getContrastYIQ(hexcolor: string): "#fff" | "#222" {
  hexcolor = hexcolor.replace("#", "");
  if (hexcolor.length === 3) {
    hexcolor = hexcolor
      .split("")
      .map((x) => x + x)
      .join("");
  }
  if (hexcolor.length !== 6) return "#222";
  // Manual overrides for saturated colors
  const lower = "#" + hexcolor.toLowerCase();
  if (["#ff0000", "#00ff00", "#0000ff"].includes(lower)) {
    return "#fff";
  }
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#222" : "#fff";
}

function formatExposureHM(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

const AcquisitionSummaryCard: React.FC<AcquisitionSummaryCardProps> = ({
  imagesForView,
  selectedTarget,
  selectedNight,
  getNightMeta,
  formatMultipleValues,
}) => {
  const { filterSettings } = useUI();
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        p: 3,
        border: "1px solid",
        borderColor: "divider",
        minHeight: 340,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flex: 1,
        height: "100%",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 2,
          display: "flex",
          alignItems: "center",
          color: "success.main",
        }}
      >
        <span style={{ marginRight: 8 }}>📊</span> Acquisition Summary
      </Typography>
      {/* Frame counts */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography color="text.secondary">Total Frames</Typography>
        <Typography fontWeight={600}>{imagesForView.length}</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography color="text.secondary">Total Exposure</Typography>
        <Typography fontWeight={600}>
          {(() => {
            const lightFrames = imagesForView.filter((img) =>
              img.FrameType ? img.FrameType === "LIGHT" : !!img.FilterName
            );
            const totalSec = lightFrames.reduce(
              (sum, img) => sum + (img.Duration || 0),
              0
            );
            return formatExposureHM(totalSec);
          })()}
        </Typography>
      </Box>
      {/* Frame type breakdown */}
      {["LIGHT", "DARK", "FLAT", "BIAS"].map((type) => (
        <Box
          key={type}
          sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
        >
          <Typography color="text.secondary">{type} Frames</Typography>
          <Typography fontWeight={600}>
            {
              imagesForView.filter((img) => {
                if ((img as any).FrameType) {
                  return (img as any).FrameType === type;
                }
                if (type === "LIGHT" && img.FilterName) return true;
                return false;
              }).length
            }
          </Typography>
        </Box>
      ))}
      {/* Exposure per filter */}
      {(() => {
        // Calculate exposure per filter for light frames
        const lightFrames = imagesForView.filter((img) =>
          img.FrameType ? img.FrameType === "LIGHT" : !!img.FilterName
        );
        const exposureByFilter: Record<string, number> = {};
        for (const img of lightFrames) {
          if (img.FilterName) {
            exposureByFilter[img.FilterName] =
              (exposureByFilter[img.FilterName] || 0) + (img.Duration || 0);
          }
        }
        const filters = Object.keys(exposureByFilter);
        if (filters.length === 0) {
          return null;
        }
        const formatExposure = formatExposureHM;
        return (
          <Box
            sx={{
              mt: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              minHeight: 48,
            }}
          >
            <Typography color="text.secondary" sx={{ fontSize: 13, mb: 0.2 }}>
              Exposure per Filter
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {filters.map((filter) => {
                const display = getFilterDisplay(filter, filterSettings);
                if (!display) return null;
                const { name, color } = display;
                const textColor = getContrastYIQ(color);
                return (
                  <Box
                    key={filter}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1.3,
                      py: 0.3,
                      borderRadius: 1,
                      bgcolor: color,
                      color: textColor,
                      fontWeight: 700,
                      fontSize: "0.9em",
                      fontFamily: "Inter, Roboto, Arial, sans-serif",
                      mr: 0.5,
                      minWidth: 48,
                      justifyContent: "space-between",
                      gap: 0.7,
                      textShadow:
                        textColor === "#fff"
                          ? "0 1px 2px rgba(0,0,0,0.25)"
                          : "0 1px 2px rgba(255,255,255,0.10)",
                      letterSpacing: 0.2,
                    }}
                  >
                    <span>{name}</span>
                    <span style={{ fontWeight: 700, marginLeft: 6 }}>
                      {formatExposure(exposureByFilter[filter])}
                    </span>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })()}
    </Box>
  );
};

export default AcquisitionSummaryCard;
